/**
 * Manage standing_instructions (Phase 6): view version history per bank + the Institute
 * baseline, add a new version, activate/deactivate (never hard-deleted - see
 * CONVENTIONS.md), import a PDF for Claude-assisted extraction into an editable draft,
 * and edit each bank's LOI-matching aliases.
 */
import { useEffect, useState } from 'react';
import { fetchAllStandingInstructions, fetchAllBanks } from '../../admin/queries';
import {
  createStandingInstructionVersion,
  updateStandingInstruction,
  activateStandingInstruction,
  deactivateStandingInstruction,
  importStandingInstructionPdf,
  createBank,
  updateBank,
  ApiError,
} from '../../api';
import type { AdminStandingInstructionRow, AdminBankRow } from '../../admin/types';
import { formatDate } from '../../lib/status';
import { Banner, Button, Chip, Spinner, Tile } from '../../components/ui';

function VersionRow({
  row,
  onChanged,
}: {
  row: AdminStandingInstructionRow;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [body, setBody] = useState(row.body);
  const [busy, setBusy] = useState<'save' | 'activate' | 'deactivate' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function saveBody() {
    setBusy('save');
    setError(null);
    try {
      await updateStandingInstruction(row.id, { body });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setBusy(null);
    }
  }

  async function toggleActive() {
    setBusy(row.active ? 'deactivate' : 'activate');
    setError(null);
    try {
      if (row.active) await deactivateStandingInstruction(row.id);
      else await activateStandingInstruction(row.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the active version.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="admin-row admin-row--stacked">
      {error ? <Banner tone="error">{error}</Banner> : null}
      <div className="admin-row__fields">
        <span className="check-row__label">
          {row.version} {row.active ? <Chip>active</Chip> : null}
        </span>
        <span className="muted">
          Effective {row.effectiveFrom ? formatDate(row.effectiveFrom) : '—'} · Added {formatDate(row.createdAt)}
          {row.sourceFilename ? ` · from ${row.sourceFilename}` : ''}
        </span>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Hide body' : 'View / edit body'}
          </Button>
          <Button variant={row.active ? 'secondary' : 'primary'} onClick={() => void toggleActive()} disabled={busy !== null}>
            {busy === 'activate' || busy === 'deactivate' ? 'Working…' : row.active ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
        {expanded ? (
          <>
            <textarea className="input note-textarea" rows={10} value={body} onChange={(e) => setBody(e.target.value)} />
            <Button onClick={() => void saveBody()} disabled={busy !== null || body === row.body}>
              {busy === 'save' ? 'Saving…' : 'Save body'}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function BankAliasesEditor({ bank, onChanged }: { bank: AdminBankRow; onChanged: () => void }) {
  const [aliases, setAliases] = useState(bank.aliases.join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await updateBank(bank.id, { aliases: aliases.split(',').map((a) => a.trim()).filter(Boolean) });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save aliases.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-row">
      {error ? <Banner tone="error">{error}</Banner> : null}
      <div className="admin-row__fields">
        <span className="check-row__label">{bank.name}</span>
        <input className="input" value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="Aliases, comma-separated" />
      </div>
      <Button onClick={() => void save()} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </div>
  );
}

export function StandingInstructionsAdmin() {
  const [instructions, setInstructions] = useState<AdminStandingInstructionRow[] | null>(null);
  const [banks, setBanks] = useState<AdminBankRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [newVersion, setNewVersion] = useState({ scope: 'baseline' as 'baseline' | string, version: '', effectiveFrom: '', body: '' });
  const [creating, setCreating] = useState(false);

  const [importScope, setImportScope] = useState<'baseline' | string>('baseline');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  const [newBank, setNewBank] = useState({ name: '', aliases: '' });
  const [creatingBank, setCreatingBank] = useState(false);

  async function load() {
    setError(null);
    try {
      const [ins, bankRows] = await Promise.all([fetchAllStandingInstructions(), fetchAllBanks()]);
      setInstructions(ins);
      setBanks(bankRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load standing instructions.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (error && !instructions) return <Banner tone="error">{error}</Banner>;
  if (!instructions) return <Spinner label="Loading standing instructions…" />;

  const baseline = instructions.filter((i) => i.isInstituteBaseline);
  const byBank = banks.map((bank) => ({ bank, versions: instructions.filter((i) => i.bankId === bank.id) }));

  async function createVersion() {
    if (!newVersion.version.trim() || !newVersion.body.trim()) {
      setError('A version label and body are required.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await createStandingInstructionVersion({
        bankId: newVersion.scope === 'baseline' ? null : newVersion.scope,
        isInstituteBaseline: newVersion.scope === 'baseline',
        version: newVersion.version.trim(),
        body: newVersion.body,
        effectiveFrom: newVersion.effectiveFrom || null,
      });
      setNewVersion({ scope: 'baseline', version: '', effectiveFrom: '', body: '' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that version.');
    } finally {
      setCreating(false);
    }
  }

  async function importPdf() {
    if (!importFile) {
      setError('Choose a PDF to import.');
      return;
    }
    setImporting(true);
    setError(null);
    try {
      await importStandingInstructionPdf({
        bankId: importScope === 'baseline' ? null : importScope,
        isInstituteBaseline: importScope === 'baseline',
        file: importFile,
      });
      setImportFile(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not import that PDF.');
    } finally {
      setImporting(false);
    }
  }

  async function addBank() {
    if (!newBank.name.trim()) {
      setError('A bank name is required.');
      return;
    }
    setCreatingBank(true);
    setError(null);
    try {
      await createBank(
        newBank.name.trim(),
        newBank.aliases.split(',').map((a) => a.trim()).filter(Boolean),
      );
      setNewBank({ name: '', aliases: '' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that bank.');
    } finally {
      setCreatingBank(false);
    }
  }

  return (
    <div className="stack">
      {error ? <Banner tone="error">{error}</Banner> : null}

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Institute baseline</h3>
        <div className="admin-rows">
          {baseline.length === 0 ? <p className="muted">No versions yet.</p> : null}
          {baseline.map((row) => (
            <VersionRow key={row.id} row={row} onChanged={() => void load()} />
          ))}
        </div>
      </Tile>

      {byBank.map(({ bank, versions }) => (
        <Tile key={bank.id} className="admin-card">
          <h3 className="checklist-cat__title">{bank.name}</h3>
          <div className="admin-rows">
            {versions.length === 0 ? <p className="muted">No bank-specific versions - the Institute baseline applies.</p> : null}
            {versions.map((row) => (
              <VersionRow key={row.id} row={row} onChanged={() => void load()} />
            ))}
          </div>
        </Tile>
      ))}

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Add a new version</h3>
        <div className="admin-new-form">
          <select className="input" value={newVersion.scope} onChange={(e) => setNewVersion((n) => ({ ...n, scope: e.target.value }))}>
            <option value="baseline">Institute baseline</option>
            {banks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <input
            className="input"
            placeholder="Version label"
            value={newVersion.version}
            onChange={(e) => setNewVersion((n) => ({ ...n, version: e.target.value }))}
          />
          <input
            className="input"
            type="date"
            value={newVersion.effectiveFrom}
            onChange={(e) => setNewVersion((n) => ({ ...n, effectiveFrom: e.target.value }))}
          />
          <textarea
            className="input note-textarea"
            rows={6}
            placeholder="Instruction body"
            value={newVersion.body}
            onChange={(e) => setNewVersion((n) => ({ ...n, body: e.target.value }))}
          />
          <Button onClick={() => void createVersion()} disabled={creating}>
            {creating ? 'Creating…' : 'Create version (inactive - activate above when ready)'}
          </Button>
        </div>
      </Tile>

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Import a PDF</h3>
        <p className="muted">
          Uploads the PDF as the audit copy and uses Claude to extract an editable draft. The draft is created inactive -
          tidy the text above, then activate it when ready.
        </p>
        <div className="admin-new-form">
          <select className="input" value={importScope} onChange={(e) => setImportScope(e.target.value)}>
            <option value="baseline">Institute baseline</option>
            {banks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <input type="file" accept="application/pdf" onChange={(e) => setImportFile(e.target.files?.[0] ?? null)} />
          <Button onClick={() => void importPdf()} disabled={importing || !importFile}>
            {importing ? 'Extracting…' : 'Import PDF'}
          </Button>
        </div>
      </Tile>

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Bank aliases (LOI lender matching)</h3>
        <div className="admin-rows">
          {banks.map((bank) => (
            <BankAliasesEditor key={bank.id} bank={bank} onChanged={() => void load()} />
          ))}
        </div>
        <div className="admin-new-form">
          <input className="input" placeholder="Bank name" value={newBank.name} onChange={(e) => setNewBank((n) => ({ ...n, name: e.target.value }))} />
          <input
            className="input"
            placeholder="Aliases, comma-separated"
            value={newBank.aliases}
            onChange={(e) => setNewBank((n) => ({ ...n, aliases: e.target.value }))}
          />
          <Button onClick={() => void addBank()} disabled={creatingBank}>
            {creatingBank ? 'Adding…' : 'Add bank'}
          </Button>
        </div>
      </Tile>
    </div>
  );
}
